import { observer } from "mobx-react-lite";

import { VariableSet } from "../Playground/types";

import ConfiguratorContent from "./ConfiguratorContent";
import { useVariableChangeNotifier } from "./hooks/useVariableChangeNotifier";
import {
  InputVariableConfiguratorStoreProvider,
  useConfiguratorStore,
} from "./input-variable-configurator-store-context";
import { Variable } from "./types";

import { cn } from "@/lib/utils";
import { RequestToolData } from "@/queries/requests/useRequestLogInputVariables";

interface InputVariableConfiguratorProps {
  inputVariables: Variable[];
  onChange?: (variables: Variable[]) => void;
  onClose: () => void;
  isLoading?: boolean;
  useSaveButton?: boolean;
  hideKey?: boolean;
  hideImportButton?: boolean;
  hideExportButton?: boolean;
  placeholder?: string;
  title?: string;
  enableAIDataset?: boolean;
  className?: string;
  promptTemplate?: string;
  onSetNameImported?: (name: string) => void;
  onRequestImported?: (
    requestId: number,
    variables: Record<string, string>,
    toolData?: RequestToolData | null,
  ) => void;
  normalizeImportedVariables?: (variables: Record<string, string>) => Record<string, string>;
  initialName?: string;
  activeSetName?: string;
  onAddVariableSet?: (variableSet: VariableSet) => void;
  currentVariableSetCount?: number;
  popoverPortalContainer?: HTMLElement | null;
}

const InputVariableConfiguratorContent = observer(
  ({
    onChange,
    onClose,
    isLoading,
    useSaveButton = false,
    hideKey = false,
    hideImportButton,
    hideExportButton = false,
    enableAIDataset = false,
    placeholder = "No input variables defined",
    promptTemplate = "",
    className,
    onSetNameImported,
    onRequestImported,
    normalizeImportedVariables,
    activeSetName,
    onAddVariableSet,
    currentVariableSetCount = 0,
    popoverPortalContainer,
  }: InputVariableConfiguratorProps) => {
    const store = useConfiguratorStore();

    useVariableChangeNotifier(store, onChange);

    return (
      <div className={cn("flex h-full flex-col", className)}>
        <ConfiguratorContent
          onClose={onClose}
          isLoading={isLoading}
          useSaveButton={useSaveButton}
          hideKey={hideKey}
          hideImportButton={hideImportButton}
          hideExportButton={hideExportButton}
          placeholder={placeholder}
          enableAIDataset={enableAIDataset}
          promptTemplate={promptTemplate}
          onSetNameImported={onSetNameImported}
          onRequestImported={onRequestImported}
          normalizeImportedVariables={normalizeImportedVariables}
          activeSetName={activeSetName}
          onAddVariableSet={onAddVariableSet}
          currentVariableSetCount={currentVariableSetCount}
          popoverPortalContainer={popoverPortalContainer}
        />
      </div>
    );
  },
);

InputVariableConfiguratorContent.displayName = "InputVariableConfiguratorContent";

const InputVariableConfigurator = (props: InputVariableConfiguratorProps) => (
  <InputVariableConfiguratorStoreProvider
    // eslint-disable-next-line react/destructuring-assignment
    initialVariables={props.inputVariables}
    // eslint-disable-next-line react/destructuring-assignment
    initialName={props.initialName}
  >
    <InputVariableConfiguratorContent {...props} />
  </InputVariableConfiguratorStoreProvider>
);

export default InputVariableConfigurator;
